import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, CheckCircle2, Check, Star } from 'lucide-react';
import { PLANS, DEFAULT_PLAN, type PlanId } from '../lib/checkout';
export function Pricing() {
  const [selectedPlan, setSelectedPlan] = useState<PlanId>(DEFAULT_PLAN);
  const activePlan = PLANS[selectedPlan];
  const handleCheckout = () => {
    window.location.href = activePlan.checkoutUrl;
  };
  return <section id="pricing" className="py-10 md:py-[60px] px-5 bg-off-white" data-id="element-147">
      <div className="max-w-[860px] mx-auto" data-id="element-148">
        <div className="text-[11px] font-extrabold tracking-[0.14em] uppercase text-blue mb-2 text-center md:text-left" data-id="element-149">
          Elegí tu plan
        </div>
        <h2 className="font-display text-[clamp(28px,6vw,52px)] text-navy mb-2 leading-tight text-center md:text-left" data-id="element-150">
          Tu DEFENDETE Megapack Ciudadano
        </h2>
        <p className="text-[14px] md:text-[16px] text-text-muted max-w-[560px] mb-5 md:mb-8 text-center md:text-left mx-auto md:mx-0" data-id="element-151">
          Ambas opciones incluyen los 4 bonos estratégicos completos.
        </p>

        {/* HORIZONTAL ROW PLAN SELECTOR — stacked vertically */}
        <div className="space-y-3 mb-5 md:mb-6" data-id="element-152">
          {(['total', 'basic'] as PlanId[]).map(planId => {
          const plan = PLANS[planId];
          const isSelected = selectedPlan === planId;
          const isFeatured = plan.featured;
          return <motion.button key={planId} onClick={() => setSelectedPlan(planId)} whileTap={{
            scale: 0.99
          }} whileHover={{
            x: 2
          }} className={`relative w-full text-left bg-white rounded-2xl overflow-hidden transition-all duration-200 border-2 ${isSelected ? isFeatured ? 'border-gold shadow-gold-glow' : 'border-blue shadow-lg' : 'border-border opacity-75 hover:opacity-100'}`} data-id="element-153">
                {/* Featured ribbon */}
                {isFeatured && <div className="bg-gold text-navy text-center py-1 text-[10px] md:text-[11px] font-extrabold tracking-[0.08em] flex items-center justify-center gap-1.5" data-id="element-154">
                    <Star size={11} fill="currentColor" data-id="element-155" /> MÁS ELEGIDO — AHORRÁS
                    $16.000
                  </div>}

                <div className="flex items-center gap-3 md:gap-4 p-3.5 md:p-5" data-id="element-156">
                  {/* Radio indicator */}
                  <div className="flex-shrink-0" data-id="element-157">
                    <div className={`w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? isFeatured ? 'bg-gold border-gold' : 'bg-blue border-blue' : 'border-border bg-white'}`} data-id="element-158">
                      {isSelected && <Check size={14} className={isFeatured ? 'text-navy' : 'text-white'} strokeWidth={3} data-id="element-159" />}
                    </div>
                  </div>

                  {/* Plan info */}
                  <div className="flex-1 min-w-0" data-id="element-160">
                    <div className="font-display text-[18px] md:text-[24px] text-navy leading-tight" data-id="element-161">
                      {plan.name}
                    </div>
                    <div className="text-[11px] md:text-[13px] text-text-muted leading-snug mt-0.5" data-id="element-162">
                      {plan.tagline}
                    </div>
                  </div>

                  {/* Prices */}
                  <div className="flex-shrink-0 text-right" data-id="element-163">
                    <div className="text-[11px] md:text-[13px] text-text-muted line-through leading-none" data-id="element-164">
                      {plan.oldPrice}
                    </div>
                    <div className={`font-display text-[26px] md:text-[38px] leading-none mt-0.5 ${isFeatured ? 'text-gold' : 'text-blue'}`} data-id="element-165">
                      {plan.price}
                    </div>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* SHARED CTA BUTTON */}
        <motion.button onClick={handleCheckout} whileTap={{
        scale: 0.98
      }} animate={selectedPlan === 'total' ? {
        boxShadow: ['0 6px 30px rgba(201,168,76,0.35)', '0 6px 40px rgba(201,168,76,0.55)', '0 6px 30px rgba(201,168,76,0.35)']
      } : {}} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className={`w-full block text-center font-display text-[18px] md:text-[22px] tracking-wide py-4 md:py-5 rounded-xl transition-all hover:-translate-y-0.5 ${selectedPlan === 'total' ? 'bg-gold text-navy' : 'bg-blue text-white shadow-lg'}`} data-id="element-166">
          💥 COMPRAR {activePlan.name} — {activePlan.price}
        </motion.button>

        <p className="text-center text-[12px] md:text-[13px] text-text-muted mt-3" data-id="element-167">
          {selectedPlan === 'total' ? 'La opción más elegida por quienes quieren la protección completa.' : 'Acceso inmediato al protocolo completo de defensa ciudadana.'}
        </p>

        {/* Trust badges */}
        <div className="flex gap-3 md:gap-5 flex-wrap mt-4 md:mt-5 justify-center" data-id="element-168">
          <span className="text-[11px] md:text-[13px] text-text-muted flex items-center gap-1.5" data-id="element-169">
            <Shield size={14} data-id="element-170" /> Pago seguro
          </span>
          <span className="text-[11px] md:text-[13px] text-text-muted flex items-center gap-1.5" data-id="element-171">
            <Zap size={14} data-id="element-172" /> Acceso en 5 min
          </span>
          <span className="text-[11px] md:text-[13px] text-text-muted flex items-center gap-1.5" data-id="element-173">
            <CheckCircle2 size={14} data-id="element-174" /> Garantía 7 días
          </span>
        </div>
      </div>
    </section>;
}